import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { TrendingUp, TrendingDown, ArrowLeft, Star, StarOff, ExternalLink, Clock, Target, Percent, AlertCircle, Zap, Menu, MessageSquare } from 'lucide-react';
import { fetchApi, formatPrice, formatPercent, formatNumber, formatDate, formatTimeAgo, getChangeColor, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';
import ShareButton from '../components/ShareButton';

export default function StockDetailPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [stock, setStock] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [selectedHorizon, setSelectedHorizon] = useState('days');
  const [news, setNews] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState('1mo');
  const [estimatedUpdate, setEstimatedUpdate] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [forumPosts, setForumPosts] = useState([]);

  const TIME_HORIZONS = [
    { id: 'hours', label: '24 Hours', short: '24H' },
    { id: 'days', label: '7 Days', short: '7D' },
    { id: 'weeks', label: '4 Weeks', short: '4W' },
    { id: 'months', label: '3 Months', short: '3M' },
    { id: 'years', label: '1 Year', short: '1Y' }
  ];

  const loadStock = useCallback(async () => {
    try {
      const data = await fetchApi(`/stocks/${ticker}`);
      setStock(data.stock);
      setPredictions(data.predictions || {});
      setNews(data.news || []);
      setEstimatedUpdate(data.estimated_next_update || null);
      
      // Also load forum posts for this stock
      try {
        const forumData = await fetchApi(`/forum/stocks/${ticker}/posts`);
        setForumPosts(forumData.posts || []);
      } catch (e) {
        // Forum posts are optional
      }
    } catch (error) {
      console.error('Failed to load stock:', error);
      toast.error('Stock not found');
    } finally {
      setLoading(false);
    }
  }, [ticker]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await fetchApi(`/stocks/${ticker}/history?period=${historyPeriod}`);
      setHistory(data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  }, [ticker, historyPeriod]);

  const checkWatchlist = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await fetchApi('/watchlist');
      const inList = data.watchlist?.some(item => item.ticker === ticker.toUpperCase());
      setInWatchlist(inList);
    } catch (error) {
      console.error('Watchlist check failed:', error);
    }
  }, [ticker, isAuthenticated]);

  useEffect(() => {
    loadStock();
    loadHistory();
    checkWatchlist();
  }, [loadStock, loadHistory, checkWatchlist]);

  const toggleWatchlist = async () => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    try {
      if (inWatchlist) {
        await fetchApi(`/watchlist/${ticker}`, { method: 'DELETE' });
        setInWatchlist(false);
        toast.success('Removed from watchlist');
      } else {
        await fetchApi(`/watchlist/${ticker}`, { method: 'POST' });
        setInWatchlist(true);
        toast.success('Added to watchlist');
      }
    } catch (error) {
      toast.error('Failed to update watchlist');
    }
  };

  const chartData = history.map(h => ({
    date: new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    price: h.close,
    volume: h.volume
  }));

  // Get current prediction based on selected horizon
  const currentPrediction = predictions[selectedHorizon];

  const probData = currentPrediction?.probability_distribution ? [
    { name: 'Rise >5%', value: (currentPrediction.probability_distribution.rise_high || 0) * 100, fill: '#10B981' },
    { name: 'Rise 0-5%', value: (currentPrediction.probability_distribution.rise_moderate || 0) * 100, fill: '#34D399' },
    { name: 'Fall 0-5%', value: (currentPrediction.probability_distribution.fall_moderate || 0) * 100, fill: '#F87171' },
    { name: 'Fall >5%', value: (currentPrediction.probability_distribution.fall_high || 0) * 100, fill: '#EF4444' }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64 lg:col-span-2" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Stock Not Found</h2>
          <p className="text-muted-foreground mb-4">The stock "{ticker}" could not be found.</p>
          <Button onClick={() => navigate('/stocks')}>Browse Stocks</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-bold text-lg sm:text-xl hidden sm:inline">MarketMind</span>
            </Link>
          </div>
          
          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <ShareButton 
              ticker={stock.ticker}
              stockName={stock.name}
              currentPrice={stock.current_price}
              prediction={currentPrediction}
            />
            <Button
              data-testid="watchlist-toggle-btn"
              variant="outline"
              onClick={toggleWatchlist}
            >
              {inWatchlist ? (
                <>
                  <StarOff className="w-4 h-4 mr-2" />
                  Remove
                </>
              ) : (
                <>
                  <Star className="w-4 h-4 mr-2" />
                  Watchlist
                </>
              )}
            </Button>
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')}>Dashboard</Button>
            ) : (
              <Button onClick={() => navigate('/auth')}>Sign In</Button>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center gap-2">
            <ShareButton 
              ticker={stock.ticker}
              stockName={stock.name}
              currentPrice={stock.current_price}
              prediction={currentPrediction}
              className="h-9 w-9 p-0 [&>span]:hidden [&>svg]:mr-0"
            />
            <Button
              data-testid="mobile-watchlist-btn"
              variant="outline"
              size="icon"
              onClick={toggleWatchlist}
            >
              {inWatchlist ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
            </Button>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-card">
                <SheetHeader>
                  <SheetTitle className="text-left">Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-6">
                  <Link 
                    to="/stocks" 
                    className="text-foreground hover:text-primary transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Stocks
                  </Link>
                  <Link 
                    to="/search" 
                    className="text-foreground hover:text-primary transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Advanced Search
                  </Link>
                  <Link 
                    to="/forum" 
                    className="text-foreground hover:text-primary transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Forum
                  </Link>
                  {isAuthenticated ? (
                    <Button 
                      onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                      className="w-full"
                    >
                      Dashboard
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                      className="w-full"
                    >
                      Sign In
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stock Header */}
        <div className="flex flex-col gap-4 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2 flex-wrap">
                <h1 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight">
                  {stock.ticker}
                </h1>
                {stock.sector && (
                  <Badge variant="outline" className="text-xs sm:text-sm">{stock.sector}</Badge>
                )}
              </div>
              <p className="text-sm sm:text-base text-muted-foreground">{stock.name}</p>
              {stock.exchange && (
                <p className="text-xs sm:text-sm text-muted-foreground">{stock.exchange} • {stock.country}</p>
              )}
            </div>
            
            <div className="flex items-center sm:items-end sm:flex-col gap-3 sm:gap-0">
              <div className="flex items-baseline gap-2 sm:gap-3">
                <span className="font-mono text-2xl sm:text-4xl font-bold">{formatPrice(stock.current_price)}</span>
                <span className={cn("font-mono text-sm sm:text-lg flex items-center", getChangeColor(stock.price_change_pct_24h))}>
                  {stock.price_change_pct_24h >= 0 ? <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-1" /> : <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />}
                  {formatPercent(stock.price_change_pct_24h)}
                </span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground sm:mt-1">
                Updated {formatTimeAgo(stock.last_updated)}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile: Prediction Card First */}
        <div className="lg:hidden mb-6 space-y-4">
          {/* Compact Prediction Summary */}
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">AI Prediction</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  <Zap className="w-3 h-3 mr-1" />
                  Auto
                </Badge>
              </div>
              
              {/* Time Horizon Pills */}
              <div className="flex gap-1 mb-3 overflow-x-auto pb-1 -mx-1 px-1">
                {TIME_HORIZONS.map(horizon => (
                  <button
                    key={horizon.id}
                    onClick={() => setSelectedHorizon(horizon.id)}
                    className={cn(
                      "shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
                      selectedHorizon === horizon.id
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/50 text-muted-foreground hover:text-foreground"
                    )}
                    data-testid={`mobile-horizon-${horizon.id}`}
                  >
                    {horizon.short}
                  </button>
                ))}
              </div>

              {currentPrediction ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {TIME_HORIZONS.find(h => h.id === selectedHorizon)?.label}
                      </p>
                      <p className={cn(
                        "font-mono text-2xl font-bold",
                        getChangeColor(currentPrediction.predicted_change_pct)
                      )}>
                        {formatPercent(currentPrediction.predicted_change_pct)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground mb-1">Confidence</p>
                      <Badge variant="outline" className={cn(
                        "font-mono text-lg px-3 py-1",
                        currentPrediction.confidence >= 70 && "border-green-500 text-green-500",
                        currentPrediction.confidence >= 50 && currentPrediction.confidence < 70 && "border-yellow-500 text-yellow-500",
                        currentPrediction.confidence < 50 && "border-muted-foreground"
                      )}>
                        {currentPrediction.confidence}%
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Mobile Analysis/Reasoning */}
                  {currentPrediction.reasoning && (
                    <div className="pt-3 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">Analysis</p>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {currentPrediction.reasoning}
                      </p>
                    </div>
                  )}
                  
                  {/* Key Factors */}
                  {currentPrediction.key_factors && currentPrediction.key_factors.length > 0 && (
                    <div className="pt-3 border-t border-border/50">
                      <p className="text-xs text-muted-foreground mb-2">Key Factors</p>
                      <div className="flex flex-wrap gap-1.5">
                        {currentPrediction.key_factors.map((factor, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {factor}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground text-sm">Prediction pending</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Key Stats - Horizontal Scroll on Mobile */}
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Card className="bg-card border-border/50 shrink-0 w-28">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Market Cap</p>
                <p className="font-mono text-sm font-semibold">{formatNumber(stock.market_cap)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50 shrink-0 w-28">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Volume</p>
                <p className="font-mono text-sm font-semibold">{formatNumber(stock.volume)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50 shrink-0 w-28">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">24h Change</p>
                <p className={cn("font-mono text-sm font-semibold", getChangeColor(stock.price_change_24h))}>
                  {formatPrice(stock.price_change_24h)}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Chart & News */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Chart */}
            <Card className="bg-card border-border/50">
              <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3">
                <CardTitle className="text-base sm:text-lg">Price History</CardTitle>
                <div className="flex gap-1 sm:gap-2 w-full sm:w-auto">
                  {['1mo', '3mo', '6mo', '1y'].map(period => (
                    <Button
                      key={period}
                      variant={historyPeriod === period ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setHistoryPeriod(period)}
                      className="flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-3"
                    >
                      {period}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div className="h-56 sm:h-80">
                  {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                        <XAxis dataKey="date" stroke="#A3A3A3" fontSize={10} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis stroke="#A3A3A3" fontSize={10} tick={{ fontSize: 10 }} domain={['auto', 'auto']} width={50} />
                        <Tooltip
                          contentStyle={{ background: '#0A0A0A', border: '1px solid #262626', borderRadius: '4px', fontSize: '12px' }}
                          labelStyle={{ color: '#FAFAFA' }}
                        />
                        <Area type="monotone" dataKey="price" stroke="#7C3AED" fillOpacity={1} fill="url(#colorPrice)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                      No historical data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* News Section */}
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base sm:text-lg">Related News</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Latest news articles affecting this stock</CardDescription>
              </CardHeader>
              <CardContent>
                {news.length === 0 ? (
                  <p className="text-muted-foreground py-6 sm:py-8 text-center text-sm">
                    No recent news available. News is gathered automatically during prediction updates.
                  </p>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {news.map((article, idx) => {
                      const currentPrediction = predictions[selectedHorizon];
                      const usedInPrediction = currentPrediction?.news_article_ids?.includes(article.article_id);
                      
                      return (
                        <div
                          key={idx}
                          className={cn(
                            "p-3 sm:p-4 border rounded-sm transition-colors",
                            usedInPrediction 
                              ? "border-primary/50 bg-primary/5" 
                              : "border-border/50 hover:border-primary/50"
                          )}
                        >
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                              {usedInPrediction && (
                                <Badge variant="default" className="text-xs py-0 px-1.5 bg-primary/20 text-primary border-0">
                                  AI Used
                                </Badge>
                              )}
                              {article.impact_magnitude && (
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs py-0 px-1.5",
                                    article.impact_magnitude === 'major' && 'border-yellow-500 text-yellow-500',
                                    article.impact_magnitude === 'moderate' && 'border-blue-500 text-blue-500',
                                    article.impact_magnitude === 'minor' && 'border-gray-500 text-gray-500'
                                  )}
                                >
                                  {article.impact_magnitude}
                                </Badge>
                              )}
                            </div>
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-sm sm:text-base hover:text-primary transition-colors flex items-start gap-2 leading-snug"
                            >
                              <span className="flex-1">{article.title}</span>
                              <ExternalLink className="w-3 h-3 mt-1 shrink-0" />
                            </a>
                            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                              <span className="truncate max-w-[100px] sm:max-w-none">{article.source}</span>
                              <span>•</span>
                              <span className="shrink-0">{formatTimeAgo(article.published_at)}</span>
                            </div>
                            {/* Multi-axis sentiment display */}
                            {(article.short_term_sentiment !== undefined || article.long_term_sentiment !== undefined) && (
                              <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs">
                                {article.short_term_sentiment !== undefined && (
                                  <span className={cn(
                                    "flex items-center gap-1",
                                    article.short_term_sentiment > 0.2 ? "text-green-500" : 
                                    article.short_term_sentiment < -0.2 ? "text-red-500" : "text-muted-foreground"
                                  )}>
                                    <span className="text-muted-foreground">Short:</span>
                                    {article.short_term_sentiment > 0 ? '+' : ''}{(article.short_term_sentiment * 100).toFixed(0)}%
                                  </span>
                                )}
                                {article.long_term_sentiment !== undefined && (
                                  <span className={cn(
                                    "flex items-center gap-1",
                                    article.long_term_sentiment > 0.2 ? "text-green-500" : 
                                    article.long_term_sentiment < -0.2 ? "text-red-500" : "text-muted-foreground"
                                  )}>
                                    <span className="text-muted-foreground">Long:</span>
                                    {article.long_term_sentiment > 0 ? '+' : ''}{(article.long_term_sentiment * 100).toFixed(0)}%
                                  </span>
                                )}
                                {article.volatility !== undefined && (
                                  <span className={cn(
                                    "flex items-center gap-1",
                                    article.volatility > 0.6 ? "text-yellow-500" : "text-muted-foreground"
                                  )}>
                                    <span className="text-muted-foreground">Vol:</span>
                                    {(article.volatility * 100).toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Forum Discussions Section */}
            <Card className="bg-card border-border/50">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-base sm:text-lg">Community Discussions</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">What traders are saying about {stock.ticker}</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/forum?ticker=${stock.ticker}`)}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                {forumPosts.length === 0 ? (
                  <div className="text-center py-6">
                    <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground mb-3 text-sm">No discussions yet</p>
                    <ShareButton 
                      ticker={stock.ticker}
                      stockName={stock.name}
                      currentPrice={stock.current_price}
                      prediction={currentPrediction}
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {forumPosts.slice(0, 3).map((post) => (
                      <div
                        key={post.post_id}
                        className="p-3 border border-border/50 rounded-sm hover:border-primary/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/forum/${post.post_id}`)}
                      >
                        <h4 className="font-medium text-sm line-clamp-1 mb-1">{post.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{post.content}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{post.author_name}</span>
                          <span>•</span>
                          <span>{formatTimeAgo(post.created_at)}</span>
                          <span className="ml-auto flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {post.comments_count || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                    {forumPosts.length > 3 && (
                      <Button 
                        variant="ghost" 
                        className="w-full text-sm"
                        onClick={() => navigate(`/forum?ticker=${stock.ticker}`)}
                      >
                        View {forumPosts.length - 3} more discussions
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Prediction (Hidden on Mobile, shown above) */}
          <div className="hidden lg:block space-y-6">
            {/* Prediction Card */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>AI Predictions</CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    <Zap className="w-3 h-3 mr-1" />
                    Auto-Updated
                  </Badge>
                </div>
                <CardDescription>
                  Predictions are automatically refreshed throughout the day
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Time Horizon Tabs */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1 p-1 bg-muted/50 rounded-sm">
                    {TIME_HORIZONS.map(horizon => (
                      <button
                        key={horizon.id}
                        onClick={() => setSelectedHorizon(horizon.id)}
                        className={cn(
                          "flex-1 min-w-[50px] px-2 py-1.5 text-xs font-medium rounded-sm transition-colors",
                          selectedHorizon === horizon.id
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                        data-testid={`horizon-${horizon.id}`}
                      >
                        {horizon.short}
                        {predictions[horizon.id] && (
                          <span className={cn(
                            "ml-1",
                            predictions[horizon.id].predicted_change_pct >= 0 ? "text-green-400" : "text-red-400"
                          )}>
                            •
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {!currentPrediction ? (
                  <div className="py-8 text-center">
                    <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">
                      Prediction pending for {TIME_HORIZONS.find(h => h.id === selectedHorizon)?.label}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      This stock is queued for AI analysis. Predictions are generated automatically.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Main Prediction */}
                    <div className="text-center p-6 rounded-sm bg-muted/50">
                      <p className="text-sm text-muted-foreground mb-2">
                        {TIME_HORIZONS.find(h => h.id === selectedHorizon)?.label} Prediction
                      </p>
                      <p className={cn(
                        "font-mono text-4xl font-bold",
                        getChangeColor(currentPrediction.predicted_change_pct)
                      )}>
                        {formatPercent(currentPrediction.predicted_change_pct)}
                      </p>
                      <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{currentPrediction.time_value} {currentPrediction.time_horizon}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Percent className="w-4 h-4 text-muted-foreground" />
                          <span>{currentPrediction.confidence}% confidence</span>
                        </div>
                      </div>
                    </div>

                    {/* Confidence Bar */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Confidence</span>
                        <span className="font-mono">{currentPrediction.confidence}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-500"
                          style={{ width: `${currentPrediction.confidence}%` }}
                        />
                      </div>
                    </div>

                    {/* Probability Distribution */}
                    {probData.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-4">Probability Distribution</p>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={probData} layout="vertical">
                              <XAxis type="number" domain={[0, 100]} stroke="#A3A3A3" fontSize={12} />
                              <YAxis type="category" dataKey="name" stroke="#A3A3A3" fontSize={11} width={70} />
                              <Tooltip
                                contentStyle={{ background: '#0A0A0A', border: '1px solid #262626', borderRadius: '4px' }}
                                formatter={(value) => `${value.toFixed(1)}%`}
                              />
                              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {probData.map((entry, index) => (
                                  <Cell key={index} fill={entry.fill} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Reasoning */}
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Analysis</p>
                      <p className="text-sm leading-relaxed">
                        {currentPrediction.reasoning || 'No detailed reasoning available.'}
                      </p>
                    </div>

                    <div className="text-center space-y-1">
                      <p className="text-xs text-muted-foreground">
                        Generated {formatTimeAgo(currentPrediction.created_at)}
                      </p>
                      {estimatedUpdate && (
                        <>
                          <p className="text-xs text-muted-foreground/70">
                            Next update: {estimatedUpdate.formatted}
                          </p>
                          <p className="text-xs text-muted-foreground/50">
                            Queue position: {estimatedUpdate.queue_position} of {estimatedUpdate.total_stocks} • ~{estimatedUpdate.cycles_per_day} updates/day
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* All Horizons Summary */}
                {Object.keys(predictions).length > 0 && (
                  <div className="mt-6 pt-6 border-t border-border/50">
                    <p className="text-sm text-muted-foreground mb-3">All Time Horizons</p>
                    <div className="grid grid-cols-5 gap-2">
                      {TIME_HORIZONS.map(horizon => {
                        const pred = predictions[horizon.id];
                        return (
                          <button
                            key={horizon.id}
                            onClick={() => setSelectedHorizon(horizon.id)}
                            className={cn(
                              "p-2 rounded-sm text-center transition-colors",
                              selectedHorizon === horizon.id ? "bg-primary/20 border border-primary" : "bg-muted/50 hover:bg-muted"
                            )}
                          >
                            <p className="text-xs text-muted-foreground">{horizon.short}</p>
                            {pred ? (
                              <p className={cn("font-mono text-sm font-bold", getChangeColor(pred.predicted_change_pct))}>
                                {pred.predicted_change_pct >= 0 ? '+' : ''}{pred.predicted_change_pct.toFixed(1)}%
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">-</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-base">Key Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Market Cap</span>
                  <span className="font-mono">{formatNumber(stock.market_cap)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Volume</span>
                  <span className="font-mono">{formatNumber(stock.volume)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">24h Change</span>
                  <span className={cn("font-mono", getChangeColor(stock.price_change_24h))}>
                    {formatPrice(stock.price_change_24h)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
